import React, { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { RootState, storeActions } from '../../../store'
import { CurrentSubView, DeviceType, UIPart } from '../../../models/enums'
import languageInMotherTongue from '../../../translations/languageCodeToMotherTongue.json'
import { capitalizeFirstLetter } from '../../../utils/capitalizeFirstLetter'
import { getMainLocale } from '../../../utils/checkLocale'

import ParameterButton from './ParameterButton'

const LanguageButton = (): JSX.Element => {
  const dispatch = useDispatch()
  const translation = useSelector((state: RootState) => state.localeReducer.currentTranslations)
  const locale = useSelector((state: RootState) => state.localeReducer.locale)
  const isRTL = useSelector((state: RootState) => state.localeReducer.isRTL)
  const supported = useSelector((state: RootState) => state.localeReducer.supported)
  const isMobile = useSelector((state: RootState) => state.viewReducer.isMobile)
  const themeColors = useSelector((state: RootState) => state.viewReducer.themeColors)
  const currentSubView = useSelector((state: RootState) => state.utilsReducer.currentSubView)
  const currentDeviceType = useSelector((state: RootState) => state.viewReducer.currentDeviceType)
  const isTactile = useSelector((state: RootState) => state.viewReducer.isTactile)
  const configuration = useSelector((state: RootState) => state.retorikReducer.configuration)

  const [mainLocale, setMainLocale] = useState<string>(getMainLocale(locale).toUpperCase())
  const [bottom, setBottom] = useState<number | string | undefined>(undefined)

  useEffect(() => {
    setMainLocale(getMainLocale(locale).toUpperCase())
  }, [locale])

  useEffect(() => {
    if (configuration.languageChoiceBottom?.setUnderRemote) {
      setBottom('-3rem')
    } else {
      switch (currentDeviceType) {
        case DeviceType.landscape: {
          if (isTactile) {
            setBottom(configuration.languageChoiceBottom?.desktopTactile || '4rem')
          } else {
            setBottom(configuration.languageChoiceBottom?.desktop || '4rem')
          }
          break
        }
        case DeviceType.borne:
          setBottom(configuration.languageChoiceBottom?.verticalTactile || '25vh')
          break
      }
    }
  }, [currentDeviceType])

  /**
   * On call :
   *  - change RetorikContext's currentSubView state
   */
  const handleClick = (e: React.MouseEvent): void => {
    dispatch(storeActions.utils.setClickedOnUI(UIPart.languages))
    dispatch(storeActions.utils.setCurrentSubView(currentSubView === CurrentSubView.languages ? CurrentSubView.none : CurrentSubView.languages))

    e.stopPropagation()
    e.preventDefault()
  }

  return supported.length > 1 ? (
    isMobile ? (
      <ParameterButton title={translation.common.lang} icon='lang' color={currentSubView === CurrentSubView.languages ? themeColors.secondary : '#A4A7AF'} onClick={(e) => handleClick(e)} />
    ) : (
      <button
        className='rf-absolute rf-z-ui rf-min-w-24 rf-right-0 rf-px-2 rf-flex rf-flex-row rf-items-center rf-rounded-l-lg'
        style={{
          bottom: bottom,
          background: 'linear-gradient(#F8F8FC, #EDEEF3)'
        }}
        onClick={handleClick}
      >
        {/* Flag */}
        <div className='rf-h-8 rf-mr-2'>
          <img className='rf-h-full' src={`https://flagsapi.com/${mainLocale}/flat/64.png`} />
        </div>

        {/* Texts */}
        <div className={`rf-flex rf-flex-col rf-items-start ${isRTL ? 'rf-text-arabic' : 'rf-text-xsm'}`}>
          <div className='rf-text-trueblack'>{capitalizeFirstLetter(translation.common.singleLang)}</div>
          <div className='rf-text-lightgray'>{capitalizeFirstLetter(languageInMotherTongue[locale.split('-')[0]])}</div>
        </div>
      </button>
    )
  ) : (
    <React.Fragment />
  )
}

export default LanguageButton
